import { call, race, take } from 'redux-saga/effects';

import { GeneratorResult, selectTyped, takeEvery, takeLatest } from '@pressreader/appstore';
import { error } from '@pressreader/logger';
import { navigateBegan, navigateDone, selectLocation, selectNavigationOrigin } from '@pressreader/navigation-core';

import { beginScrollRestoreSuppression, endScrollRestoreSuppressionSoon, getScrollState, persistScrollPosition } from '../../utils/scroll';
import { abortScrollRestoreAction } from '../actions';

/** Chase timings — mutable ONLY as a test seam (the fake-timer spec shrinks them, or stepping
 *  hundreds of 16ms frames per run blows the real-time test timeout); production always runs
 *  the defaults. */
const timings = {
    /** Hard bound on the whole restore — the chase never runs longer, growing or not. */
    maxRestoreDurationMs: 5000,
    /** A chase whose final stretch brought neither height growth nor target progress for this
     *  long ended genuinely stalled (feeds the short-page memo) rather than merely cut off
     *  mid-progress. */
    maxStallDurationMs: 1500,
};
/** Consecutive frames the window must hold the target position before the restore is considered done. */
const settleFrameCount = 3;
/** Sub-pixel scroll offsets and zoom rounding make exact equality unreliable. */
const positionEpsilonPx = 2;
/** rAF does not fire in hidden tabs; without a timer fallback a restore would park there forever. */
const hiddenTabFramePollMs = 250;

/** Any of these means the user took over scrolling — programmatic `window.scrollTo` fires none of them. */
const userInputEvents = ['wheel', 'touchstart', 'pointerdown', 'mousedown', 'keydown'] as const;

const waitForNextFrame = () =>
    new Promise<void>(resolve => {
        const timeout = window.setTimeout(() => {
            cancelAnimationFrame(frame);
            resolve();
        }, hiddenTabFramePollMs);
        const frame = requestAnimationFrame(() => {
            window.clearTimeout(timeout);
            resolve();
        });
    });

let claimedByView = false;
let abortActiveRestore: (() => void) | null = null;

/**
 * A view that restores scroll on its own (e.g. the feed views' row-anchored restore) calls this
 * to take the current navigation over from the generic history-position restore: a running
 * generic restore stops immediately and none starts later in this navigation. The claim expires
 * at the next `navigateBegan`.
 *
 * The claim carries no navigation identity — it applies to whatever navigation is current when
 * it fires — so claim synchronously while handling your own `navigateDone`. A handler that
 * waits first (feed-ready waits, render timeouts) must cancel itself when a newer navigation
 * begins (race the wait against `navigateBegan` — see the bookmarks feed saga), or its late
 * claim aborts a restore belonging to the next page.
 */
function claimScrollRestore(): void {
    claimedByView = true;
    abortActiveRestore?.();
}

/**
 * Scrolls a freshly navigated view to the top. Views call this from their `navigateBegan`
 * handler instead of a bare `scrollTo(0, 0)`: a back/forward into an entry with a saved
 * position must keep the window where the restore saga is about to put it. The origin check
 * also covers configs spread from an old entry, which can carry that entry's stale saved
 * position into a pushed navigation — those still start at the top.
 *
 * Calling this is also the view's scroll-ownership declaration: the route layer reset
 * `scrollRestoration` to `'auto'` just before `navigateBegan`, and without re-declaring
 * `'manual'` here the `AppRefocus` navigation default scrolls the window to top after
 * `navigateDone` — a single external write is enough to end a running restore chase on a
 * page whose height has stopped changing (e.g. a navCache hit).
 */
function* resetScrollUnlessRestoring(): GeneratorResult<void> {
    if (window.history) {
        window.history.scrollRestoration = 'manual';
    }
    const origin = yield* selectTyped(selectNavigationOrigin);
    const location = yield* selectTyped(selectLocation);
    if (!(origin === 'POP' && getScrollState(location.state))) {
        requestAnimationFrame(() => window.scrollTo(0, 0));
    }
}

/*
    A single scrollTo after navigateDone cannot restore into views that build their height
    gradually: virtualized grids only give real heights to rows their render walk has
    touched (react-virtualized estimates the rest at 30px), and infinite-scroll pages only
    hold the first batch — either way the document is far shorter than it was when the
    position was saved, so one early scrollTo clamps screens above the target (bug 169701).

    Instead the restore chases the target frame by frame: each growth of the document raises
    the clamp, and writing the raised target walks more virtualized rows / triggers the next
    batch load, until the exact saved position is reached and holds. The loop yields to the
    user and to another writer, and gives up at a hard time cap. The live saver is suppressed for the whole
    chase (its own scrollTo calls fire real scroll events, and maxWait would flush transient
    positions mid-chase), and every exit that did NOT reach the target re-persists it into
    the entry, so the target survives failed attempts.

    Quietness mid-chase is never terminal: a batch on a slow connection outlasts any fixed
    stall window (neither the height nor the distance moves while it downloads), so the chase
    keeps watching for its whole budget and a late batch re-arms it by simply landing. A
    target the page can no longer reach (content genuinely shrank — including pages that
    still grow their virtualized rows but top out short of the target) converges via a
    high-water memo: a chase that ran out its budget with a quiet tail, parked at the clamp,
    records the achieved maximum, and a second consecutive such stall that got no higher
    persists the achieved clamp in place of the target — the entry converges to a reachable
    position even if the user never scrolls again (the run-start seed re-implants the target
    on every run, so merely skipping the exit write would not converge). One grace attempt per
    high-water mark covers transiently short pages (an error page, a slow first batch):
    real cross-visit progress renews the grace, and a reached target clears the memo. The
    memo and the entry-ownership guard key on the backend-minted `entryKey` — unique per
    history entry AND stable across writes (`location.key` is re-minted by every
    replaceState; the nav config's `cacheKey` is stable but shared — the skip-navigation
    path pushes article-popup entries that reuse it, and those must not inherit the view's
    target or keep its chase alive).
*/
const shortRestoreMemoCap = 8;
const shortRestoreMemo = new Map<string, { y: number; achievedMaxY: number }>();

const readCurrentEntryKey = (): string | undefined => (window.history.state as { entryKey?: string } | null | undefined)?.entryKey;

async function performScrollRestore(x: number, y: number, signal: AbortSignal, entryKey?: string): Promise<void> {
    let interrupted = false;
    const interrupt = () => {
        interrupted = true;
    };
    // Exact identity only. An undefined entryKey (a pre-deploy entry, a host page, unit tests)
    // matches only an equally unkeyed current entry: every post-deploy push mints a key, so a
    // popup pushed over an unkeyed view still flips the comparison and stops the chase. The
    // residual blind spot — a skip-navigation switch between two PRE-deploy entries — retires
    // as entries are revisited (router replaces heal a missing key with a fresh mint).
    const entryIsCurrent = () => readCurrentEntryKey() === entryKey;

    let reachedTarget = false;
    let stalledQuiet = false;
    let suppression: number | null = null;

    // The latch and the listeners engage inside the try: the finally must run on ANY throw,
    // or scroll saving stays suppressed (and the listeners leak) for the rest of the session.
    try {
        userInputEvents.forEach(event => window.addEventListener(event, interrupt, { passive: true }));
        suppression = beginScrollRestoreSuppression();
        // Re-seed the target up front: a router replaceState may have rebuilt the entry state
        // without the scroll key, and the saver hook's mount write may have recorded the
        // pre-restore position — this write also cancels that one if it is still pending.
        if (entryIsCurrent()) {
            persistScrollPosition(x, y);
        }

        // Let the navigation's React commit land before the first write: at navigateDone the
        // dispatch is still on the stack and the document may still show the previous content.
        await waitForNextFrame();

        const startedAt = performance.now();
        let lastProgressAt = startedAt;
        let lastAppliedY: number | null = null;
        let lastScrollHeight: number | null = null;
        let maxScrollHeight = 0;
        let minDistance = Number.POSITIVE_INFINITY;
        let settledFrames = 0;
        let externalMoveFrames = 0;

        for (;;) {
            // The ownership check ends the chase when an entry is pushed over the view (article
            // popup via the skip-navigation path): no navigateBegan fires there, so neither the
            // saga's race nor a user-input event would stop the loop across the entry switch.
            if (interrupted || signal.aborted || !entryIsCurrent()) {
                return;
            }
            const now = performance.now();
            if (now - startedAt > timings.maxRestoreDurationMs) {
                // A quiet spell mid-chase is NOT terminal — a catalog batch on a slow connection
                // outlasts any fixed stall window, and returning early would leave nothing
                // watching when it lands (the shape QA reopened 169701 for). Quietness is judged
                // only here, at the end of the budget: a run whose final stretch saw no progress
                // genuinely could not reach the target and may feed the short-page memo, while a
                // run cut off mid-progress must not.
                stalledQuiet = now - lastProgressAt > timings.maxStallDurationMs;
                return;
            }

            const scrollHeight = document.documentElement.scrollHeight;
            const maxY = Math.max(0, scrollHeight - window.innerHeight);
            const target = Math.min(y, maxY);
            const currentY = window.scrollY;
            const distance = Math.abs(currentY - y);

            if (distance <= positionEpsilonPx) {
                settledFrames += 1;
                if (settledFrames >= settleFrameCount) {
                    reachedTarget = true;
                    return;
                }
            } else {
                settledFrames = 0;
                const movedExternally = lastAppliedY !== null && Math.abs(currentY - lastAppliedY) > positionEpsilonPx;
                if (movedExternally) {
                    externalMoveFrames += 1;
                    // A single deviating frame during layout churn can be the browser clamping or
                    // anchoring around a reflow — re-applying recovers it. A writer that wins on a
                    // quiet-layout frame, or two frames in a row, owns the position.
                    if (scrollHeight === lastScrollHeight || externalMoveFrames >= 2) {
                        return;
                    }
                } else {
                    externalMoveFrames = 0;
                }
                // 'instant' keeps the write synchronous even if a theme sets scroll-behavior:
                // smooth on the root — an animated write would read as an external move above.
                window.scrollTo({ left: x, top: target, behavior: 'instant' });
                lastAppliedY = target;
            }

            // Only a NEW height maximum or distance minimum counts as progress: comparing
            // against the previous frame would let an oscillating height (late images, ads)
            // re-stamp progress on every uptick, so the cap exit above would never see a quiet
            // tail and such a page could never converge via the memo. A shrink is content
            // being replaced, not progress toward the target.
            if (scrollHeight > maxScrollHeight || distance < minDistance) {
                lastProgressAt = now;
            }
            maxScrollHeight = Math.max(maxScrollHeight, scrollHeight);
            minDistance = Math.min(minDistance, distance);
            lastScrollHeight = scrollHeight;

            await waitForNextFrame();
        }
    } finally {
        try {
            if (reachedTarget) {
                if (entryKey !== undefined) {
                    shortRestoreMemo.delete(entryKey);
                }
            } else if (!signal.aborted && entryIsCurrent()) {
                // Unreached target: keep it recoverable for the next back/forward visit — the exit
                // stranded the page at a transient offset that must not become the record. Not on
                // signal aborts: a takeover (view claim, abortScrollRestoreAction) hands the entry
                // to a writer whose own scrolls should record it, and a navigation abort must not
                // write at all. Not into a foreign entry either: the ownership check keeps a popup
                // pushed over the view (same nav config, its own entry) from inheriting the target.
                const scrollHeight = document.documentElement.scrollHeight;
                const achievedMaxY = Math.max(0, scrollHeight - window.innerHeight);
                const parkedShort =
                    stalledQuiet &&
                    entryKey !== undefined &&
                    achievedMaxY < y - positionEpsilonPx &&
                    Math.abs(window.scrollY - achievedMaxY) <= positionEpsilonPx;
                const mark = entryKey !== undefined ? shortRestoreMemo.get(entryKey) : undefined;
                const repeatShortStall = parkedShort && mark !== undefined && mark.y === y && achievedMaxY <= mark.achievedMaxY + positionEpsilonPx;
                if (!repeatShortStall) {
                    persistScrollPosition(x, y);
                } else {
                    // Second consecutive stall at (or below) the recorded high water: the target
                    // is genuinely unreachable here. Record the achieved clamp instead — the
                    // run-start seed re-implanted the target, so merely skipping this write would
                    // leave the stale y in the entry, and a user who never scrolls would re-run
                    // the full chase on every POP forever. The next Back restores to the clamp,
                    // reaches it, and clears the memo.
                    persistScrollPosition(x, achievedMaxY);
                }
                if (stalledQuiet && entryKey !== undefined) {
                    if (parkedShort) {
                        if (!shortRestoreMemo.has(entryKey) && shortRestoreMemo.size >= shortRestoreMemoCap) {
                            shortRestoreMemo.clear();
                        }
                        // Keep the MAXIMUM: a visit that tops out lower must not lower the mark,
                        // or a clamp oscillating across visits (late images/ads) renews the grace
                        // forever and the memo never converges.
                        shortRestoreMemo.set(entryKey, { y, achievedMaxY: mark?.y === y ? Math.max(mark.achievedMaxY, achievedMaxY) : achievedMaxY });
                    } else {
                        shortRestoreMemo.delete(entryKey);
                    }
                }
            }
        } finally {
            // Unconditional teardown — throw-safe against the exit bookkeeping above too.
            userInputEvents.forEach(event => window.removeEventListener(event, interrupt));
            if (suppression !== null) {
                // Ownership token: a superseded run's late finally (the loop sees the abort one
                // frame late) must not lift the suppression the replacing run already holds.
                endScrollRestoreSuppressionSoon(suppression);
            }
        }
    }
}

function* restoreScrollPosition() {
    const origin = yield* selectTyped(selectNavigationOrigin);
    if (origin !== 'POP' || claimedByView) {
        // Only back/forward restores: fresh entries have no position of their own, and configs
        // pushed from a spread of an old config can carry that entry's stale position along.
        return;
    }
    const location = yield* selectTyped(selectLocation);
    const scrollState = getScrollState(location.state);
    if (!scrollState) {
        return;
    }
    const { x, y } = scrollState;
    // The backend-minted entry identity: unique per history entry, carried across replaces
    // (location.key is re-minted by every replaceState; the nav config's cacheKey is shared
    // with popup entries pushed over this view).
    const entryKey = location.entryKey;
    const abortController = new AbortController();
    const abort = () => abortController.abort();
    abortActiveRestore = abort;
    try {
        yield race({
            restored: call(performScrollRestore, x, y, abortController.signal, entryKey),
            interrupted: take(navigateBegan),
            takenOver: take(abortScrollRestoreAction),
        });
    } catch (err) {
        // Best-effort subsystem: a throw here would ride takeLatest into the forked
        // views-common root saga and take down every saga under it.
        error(err);
    } finally {
        // Reached on interruption and on takeLatest cancellation — the promise loop
        // cannot be cancelled from outside, it only sees this signal.
        abortController.abort();
        if (abortActiveRestore === abort) {
            abortActiveRestore = null;
        }
    }
}

const clearScrollRestoreClaim = () => {
    claimedByView = false;
};

/** Test-only: the high-water memo is module state and would otherwise couple specs to each other. */
const resetScrollRestoreMemoForTests = () => {
    shortRestoreMemo.clear();
};

function* onNavigateBegan() {
    yield call(clearScrollRestoreClaim);
}

function* saga() {
    yield takeEvery(navigateBegan, onNavigateBegan);
    yield takeLatest(navigateDone, restoreScrollPosition);
}

export {
    claimScrollRestore,
    performScrollRestore,
    resetScrollRestoreMemoForTests,
    resetScrollUnlessRestoring,
    saga,
    timings as scrollRestoreTimingsForTests,
};
